import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import axios from 'axios';
import { TRIPLE_WHALE_API, TRIPLE_WHALE_STATES } from '../consts/triplewhale';

const initialState = {
  data: null,
  dataByDate: {},
  loading: false,
  error: null,
  lastFetchedDate: null,
  lastFetchedTime: null
};

// Async thunk for fetching Triple Whale data
export const fetchTripleWhaleData = createAsyncThunk(
  'tripleWhale/fetchData',
  async ({ date, refresh = false }, { rejectWithValue }) => {
    try {
      const response = await axios.get(TRIPLE_WHALE_API.ENDPOINTS.SUMMARY, {
        params: { date, refresh }
      });
      
      if (response.data.success) {
        toast.success('Triple Whale data fetched successfully');
        return {
          data: response.data.data,
          date: date,
          timestamp: new Date().toISOString()
        };
      } else {
        throw new Error('Failed to fetch Triple Whale data');
      }
    } catch (error) {
      // Skip error handling if request was cancelled (e.g., route change)
      if (error.code === 'ERR_CANCELED' || error.message === 'canceled' || error.name === 'AbortError' || error.name === 'CanceledError') {
        throw new Error('AbortError');
      }
      
      console.error('Triple Whale fetch error:', error);
      
      // Handle different error types with appropriate user messages
      let errorMessage = 'Failed to fetch Triple Whale analytics data';
      
      if (error.response) {
        switch (error.response.status) {
          case 403:
            errorMessage = 'You do not have permission to access Triple Whale analytics';
            break;
          case 400:
            errorMessage = 'Invalid date format provided';
            break;
          case 429:
            errorMessage = 'Too many requests. Please try again later';
            break;
          case 500:
            errorMessage = 'Triple Whale service is currently unavailable';
            break;
          default:
            errorMessage = error.response.data?.details || errorMessage;
        }
      } else if (error.request) {
        errorMessage = 'Network error. Please check your connection';
      }
      
      toast.error(errorMessage);
      return rejectWithValue({
        message: errorMessage,
        status: error.response?.status || 'NETWORK_ERROR'
      });
    }
  }
);

export const tripleWhaleSlice = createSlice({
  name: 'tripleWhale',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearData: (state) => {
      state.data = null;
      state.dataByDate = {};
      state.error = null;
      state.lastFetchedDate = null;
      state.lastFetchedTime = null;
    },
    setError: (state, action) => {
      state.error = action.payload;
      state.loading = false;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTripleWhaleData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTripleWhaleData.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.data = action.payload.data;
        state.dataByDate[action.payload.date] = action.payload.data;
        state.lastFetchedDate = action.payload.date;
        state.lastFetchedTime = action.payload.timestamp;
      })
      .addCase(fetchTripleWhaleData.rejected, (state, action) => {
        // Skip error state update if request was cancelled
        if (action.error?.message === 'AbortError') {
          state.loading = false;
          return;
        }
        
        state.loading = false;
        state.error = action.payload || { 
          message: 'Unknown error occurred', 
          status: 'UNKNOWN' 
        };
        state.data = null;
      });
  }
});

// Action creators
export const { clearError, clearData, setError } = tripleWhaleSlice.actions;

// Selectors
export const selectTripleWhaleData = (state) => state.tripleWhale.data;
export const selectTripleWhaleLoading = (state) => state.tripleWhale.loading;
export const selectTripleWhaleError = (state) => state.tripleWhale.error;
export const selectTripleWhaleDataByDate = (state, date) =>
  state.tripleWhale.dataByDate[date] || null;
export const selectTripleWhaleLastFetched = (state) => ({
  date: state.tripleWhale.lastFetchedDate,
  time: state.tripleWhale.lastFetchedTime
});

// Check if data exists for a specific date
export const selectHasDataForDate = (state, date) =>
  !!state.tripleWhale.dataByDate[date];

export default tripleWhaleSlice.reducer; 